import { AlarmIcon, CheckIcon, makeStyles, Theme, ThumbUpIcon } from '@freighthub/silhouette-core'
import { isEqual } from 'lodash-es'
import { FC } from 'react'
import { useI18n } from 'utils/localisation'

import { EventCode, IShipTpEvent } from '../domain/TransportPlanEvent'
import { formatTpTimestampToString } from '../service/format'
import { TPTimestampSingle } from './TPTimestamp'

const TpEventsNames: Record<EventCode, string> = {
  crd: 'transport_plan-crd',
  cep: 'transport_plan-cep',
  cas: 'transport_plan-cas',
  cds: 'transport_plan-cds',
  dpr: 'transport_plan-dpr',
  vgm: 'transport_plan-vgm',
  cso: 'transport_plan-cso',
  sps: 'transport_plan-sps',

  cgi: 'transport_plan-cgi',
  cll: 'transport_plan-cll',
  vdl: 'transport_plan-vdl',
  vat1: 'transport_plan-vat1',
  vdt1: 'transport_plan-vdt1',
  vat2: 'transport_plan-vat2',
  vdt2: 'transport_plan-vdt2',
  vat3: 'transport_plan-vat3',
  vdt3: 'transport_plan-vdt3',
  vat4: 'transport_plan-vat4',
  vdt4: 'transport_plan-vdt4',
  vad: 'transport_plan-vad',
  cdd: 'transport_plan-cdd',
  cgo: 'transport_plan-cgo',
  aoa: 'transport_plan-aoa',
  ado: 'transport_plan-ado',
  aad: 'transport_plan-aad',
  rcf: 'transport_plan-rcf',
  dlv: 'transport_plan-dlv',

  sdc: 'transport_plan-sdc',
  csd: 'transport_plan-csd',
  cdc: 'transport_plan-cdc',
  ced: 'transport_plan-ced',
  cer: 'transport_plan-cer',
  cgimo: 'transport_plan-cgimo',
  cgimd: 'transport_plan-cgimd',
  cgomd: 'transport_plan-cgomd',
  cgomo: 'transport_plan-cgomo',

  [EventCode.VDD]: 'transport_plan-vdd',

  [EventCode.RAIL_PICKUP]: 'transport_plan-rail-pickup',
  [EventCode.RAIL_DEPARTURE]: 'transport_plan-rail-departure',
  [EventCode.RAIL_ARRIVAL]: 'transport_plan-rail-arrival',
  [EventCode.RAIL_DELIVERY]: 'transport_plan-rail-delivery',
  [EventCode.RAIL_CARGO_READY]: 'transport_plan-crd',

  [EventCode.AIRPORT_ARIVAL_1]: 'transport_plan-transit-airport-arrival-1',
  [EventCode.AIRPORT_ARIVAL_2]: 'transport_plan-transit-airport-arrival-2',
  [EventCode.AIRPORT_ARIVAL_3]: 'transport_plan-transit-airport-arrival-3',
  [EventCode.AIRPORT_ARIVAL_4]: 'transport_plan-transit-airport-arrival-4',
  [EventCode.AIRPORT_DEPARTURE_1]: 'transport_plan-transit-airport-departure-1',
  [EventCode.AIRPORT_DEPARTURE_2]: 'transport_plan-transit-airport-departure-2',
  [EventCode.AIRPORT_DEPARTURE_3]: 'transport_plan-transit-airport-departure-3',
  [EventCode.AIRPORT_DEPARTURE_4]: 'transport_plan-transit-airport-departure-4',
}

const useStyles = makeStyles<Theme>((theme) => ({
  row: {
    display: 'table-row',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  cell: {
    display: 'table-cell',
    padding: theme.spacing(0.25, 1),
  },
  eventStatusIcon: {
    opacity: 0.75,
    verticalAlign: 'text-bottom',
    marginRight: theme.spacing(0.5),
    fontSize: theme.typography.subtitle1.fontSize,
  },
}))

interface EventStatusProps {
  event: IShipTpEvent
  done: boolean
}

const EventStatus: FC<EventStatusProps> = ({ event, done }) => {
  const classes = useStyles()
  const { translate } = useI18n()

  if (!event.aggregatedTimestamp) {
    return null
  }

  if (done) {
    return (
      <div>
        <CheckIcon className={classes.eventStatusIcon} />
        {translate('shipment_details-containers-pdad-status_done')}
      </div>
    )
  }
  if (event.aggregatedTimestamp.confirmed) {
    return (
      <div>
        <ThumbUpIcon className={classes.eventStatusIcon} />
        {translate('shipment_details-containers-pdad-status_confirmed')}
      </div>
    )
  }
  return (
    <div>
      <AlarmIcon className={classes.eventStatusIcon} />
      {translate('shipment_details-containers-pdad-status_estimated')}
    </div>
  )
}

interface ContainerEventRowProps {
  event: IShipTpEvent
  eventCode: EventCode
  done: boolean
}

export const ContainerEventRow: FC<ContainerEventRowProps> = ({ done, event, eventCode }) => {
  const classes = useStyles()
  const { translate, translateIfExists } = useI18n()
  const aggregatedTimestamp = event && event.aggregatedTimestamp
  const revisedTimestamp = event && event.revisedTimestamp
  const isAggregatedInitial = isEqual(revisedTimestamp, aggregatedTimestamp)
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ITimestampJSON | undefined' is n... Remove this comment to see the full error message
  const initial = formatTpTimestampToString(translate, revisedTimestamp)

  const updated =
    event && isAggregatedInitial
      ? null
      : aggregatedTimestamp &&
        !isAggregatedInitial && (
          <TPTimestampSingle
            // @ts-expect-error TS(2322) FIXME: Type 'ITimestampJSON | undefined' is not assignabl... Remove this comment to see the full error message
            aggregatedTimestamp={event.aggregatedTimestamp}
            // @ts-expect-error TS(2322) FIXME: Type 'ITimestampJSON | undefined' is not assignabl... Remove this comment to see the full error message
            revisedTimestamp={event.revisedTimestamp}
          />
        )

  return (
    <div className={classes.row}>
      <div className={classes.cell}>{translate(TpEventsNames[eventCode])}</div>
      <div className={classes.cell}>
        <EventStatus event={event} done={done} />
      </div>
      <div className={classes.cell}>{initial}</div>
      <div className={classes.cell}>{updated}</div>
      <div className={classes.cell}>{aggregatedTimestamp?.reason && translateIfExists(aggregatedTimestamp.reason)}</div>
    </div>
  )
}
